import { useEffect, useMemo } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { toApiDate } from "@marekl/adminer/datetime";
import {
  useAddExistingTeamMember,
  useUpdateMembership,
  useUpdatePerson,
  type Membership,
  type Person,
  type PersonPayload,
  type UpdateMembershipPayload,
} from "../../../lib/queries/memberships";
import {
  leaderSchema,
  memberSchema,
  personEqual,
  personToFormValues,
  IDENTITY_FIELDS,
  LEADER_ROLE_OPTIONS,
  ROLE_OPTIONS,
  type IdentityField,
  type IdentityLock,
  type MemberFormOutput,
  type MemberFormValues,
} from "./memberFormSchema";
import { MemberFormFields } from "./MemberFormFields";
import { PhotoUploadField } from "../../../components/PhotoUploadField";
import type { ID } from "../../../lib/id";

function rolesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((r) => set.has(r));
}

function toFormValues(person: Person, membership: Membership | null): MemberFormValues {
  return {
    person: personToFormValues(person),
    roles: [...(membership?.roles ?? [])],
    // A leader with no roster row yet is being put on the soupiska, which is
    // a registration; otherwise follow the row we are editing.
    membership_kind: membership?.kind ?? "registered",
  };
}

/**
 * EditMemberModal is the single edit surface for anyone on a team — the
 * roster rows and the „Vedoucí" card both open it, so the same person is
 * never edited through two dialogs with different fields and different save
 * semantics.
 *
 * `isLeader` only relaxes the role rules (empty set allowed, Asistent
 * hidden) and shows the read-only „Vedoucí" marker; identity fields, photo
 * upload, and registration-window locking behave identically for everyone.
 *
 * `membership` is null when the leader has no roster row for the season yet
 * — see onSubmit for how that is resolved.
 */
export function EditMemberModal({
  teamId,
  person,
  membership,
  isLeader = false,
  open,
  onClose,
  identityLock = null,
  lockRoles = false,
  hideKind = false,
}: {
  teamId: ID;
  person: Person;
  membership: Membership | null;
  isLeader?: boolean;
  open: boolean;
  onClose: () => void;
  // Why this person's identity is restricted; see IdentityLock. The dialog
  // turns it into the set of fields to freeze, which for the ordinary
  // „filled" case is only the ones that already hold a value.
  identityLock?: IdentityLock;
  lockRoles?: boolean;
  // hideKind drops „Typ členství" from the dialog. Set for anyone below
  // members_admin: whether a person is a registered ČNSMS member is a
  // federation decision, not a team leader's (the backend enforces it with
  // ErrMembershipKindForbidden). Also set by „Registrace do sezóny", which
  // registers členy and nothing else.
  hideKind?: boolean;
}) {
  const lockedIdentity = useMemo<ReadonlySet<IdentityField>>(() => {
    if (identityLock === null) return new Set();
    if (identityLock !== "filled") return new Set(IDENTITY_FIELDS);
    // Mirrors identityAlteredFields on the server: a value already on file is
    // frozen, a gap stays open so an incomplete record can still be completed.
    return new Set(
      IDENTITY_FIELDS.filter((f) => (f === "birth_date" ? !!person.birth_date : !!person[f])),
    );
  }, [identityLock, person]);

  const updatePerson = useUpdatePerson(person.id, teamId);
  const updateMembership = useUpdateMembership(membership?.id ?? "");
  const addExisting = useAddExistingTeamMember(teamId);

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<MemberFormValues, unknown, MemberFormOutput>({
    resolver: zodResolver(isLeader ? leaderSchema : memberSchema) as Resolver<
      MemberFormValues,
      unknown,
      MemberFormOutput
    >,
    defaultValues: toFormValues(person, membership),
    mode: "onTouched",
  });

  useEffect(() => {
    if (open) reset(toFormValues(person, membership));
  }, [open, person, membership, reset]);

  // Drives conditional requiredness of rodné číslo + address.
  const watchedKind = watch("membership_kind");

  async function onSubmit(values: MemberFormOutput) {
    const personPayload: PersonPayload = {
      ...values.person,
      birth_date: toApiDate(values.person.birth_date) ?? "",
    };
    const kindChanged = !!membership && values.membership_kind !== membership.kind;
    const membershipPayload: UpdateMembershipPayload = {
      roles: values.roles,
      ...(kindChanged ? { kind: values.membership_kind } : {}),
    };

    try {
      // Deliberately sequential rather than Promise.all: a promotion to
      // „Člen" is validated against the STORED person, so the newly supplied
      // rodné číslo and address must land first or the backend rejects the
      // promotion with data the user has already typed in.
      if (!personEqual(personPayload, person)) {
        await updatePerson.mutateAsync(personPayload);
      }
      if (membership) {
        // An empty role set is a valid save for the leader — it means
        // „pouze vedoucí". The row is kept either way; removing the leader
        // from the roster is rejected by the backend.
        if (kindChanged || !rolesEqual(values.roles, membership.roles)) {
          await updateMembership.mutateAsync(membershipPayload);
        }
      } else if (values.roles.length > 0) {
        // Leader with no roster row who is being given a competitive role.
        // With no roles there is nothing to create: an empty-role row would
        // flip a team that never registered anyone out of „Chybí registrace".
        await addExisting.mutateAsync({
          person_id: person.id,
          roles: values.roles,
          kind: values.membership_kind,
        });
      }
      onClose();
    } catch (err) {
      apiErrorToFormErrors<MemberFormValues>(err, setError, {
        fieldMap: (field) => (field === "roles" ? "roles" : `person.${field}`) as never,
        fallbackMessage: "Změny se nepodařilo uložit.",
      });
    }
  }

  const rootMessage = rootErrorMessage(errors);

  const pending = updatePerson.isPending || updateMembership.isPending || addExisting.isPending;

  return (
    <FormModal
      open={open}
      title={`Upravit člena: ${person.first_name} ${person.last_name}`}
      submitLabel="Uložit změny"
      busy={pending}
      error={rootMessage}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      width="lg"
    >
      <MemberFormFields
        register={register}
        control={control}
        errors={errors}
        roleOptions={isLeader ? LEADER_ROLE_OPTIONS : ROLE_OPTIONS}
        showLeaderNote={isLeader}
        identityLock={identityLock}
        lockedIdentity={lockedIdentity}
        lockRoles={lockRoles}
        // The leader is the team's ČNSMS-accountable contact and is always a
        // registered member — the backend returns ErrLeaderMustBeRegistered
        // on a demotion attempt.
        // The leader is always a registered member, so even a
        // members_admin sees their kind pinned (ErrLeaderMustBeRegistered).
        lockKind={isLeader}
        hideKind={hideKind}
        kind={watchedKind}
      />
      {watchedKind === "registered" && (
        // Photo upload is a standalone side action (its own mutation),
        // deliberately independent of the RHF submit above.
        <PhotoUploadField
          teamId={teamId}
          personId={person.id}
          hasPhoto={person.photo_uploaded ?? false}
        />
      )}
    </FormModal>
  );
}
